import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Globe2, Lightbulb, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Slider } from '../ui/slider';
import { CineCombo } from './CineCombo';
import { AdaptationPicker } from './AdaptationPicker';
import { CreditPill } from '../AIStudioShared';
import { errMsg, startAutoDirect } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const Seg = ({ value, options, onChange, testId }) => (
  <div className="inline-flex rounded-lg border border-neutral-800 bg-neutral-950 p-0.5">
    {options.map(([v, label, Icon]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={cn('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors',
          value === v ? 'bg-amber-500 text-neutral-900' : 'text-neutral-400 hover:text-neutral-200')}>
        {Icon && <Icon size={13} />} {label}
      </button>
    ))}
  </div>
);

export function AutoDirectorPanel({ project, catalog, onStarted }) {
  const [source, setSource] = useState(project.adaptation?.source_id ? 'adapt' : 'original');
  const [form, setForm] = useState({
    idea: '', format: project.format || 'film', episodes_count: project.episodes_count || 1,
    scenes_per_episode: 6, scene_duration: 6, genre: project.genre || 'Drama', tone: project.tone || 'cinematic',
    language: project.language || 'English', style_preset: project.style_preset || 'prestige-drama',
  });
  const prev = project.adaptation || {};
  const [adapt, setAdapt] = useState({
    source_id: prev.source_id?.startsWith('custom:') ? '' : prev.source_id || '',
    source_title: prev.source_id?.startsWith('custom:') ? prev.source_title || '' : '',
    culture_id: prev.culture_id || '', twist_level: prev.twist_level || 'balanced', originality_confirmed: false,
  });
  const [sending, setSending] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const running = project.direction?.status === 'running';
  const eps = form.format === 'series' ? form.episodes_count : 1;
  const shots = eps * form.scenes_per_episode;
  const runtime = shots * form.scene_duration;
  const languages = useMemo(() => [...new Set(['English', ...catalog.cultures.map((c) => c.language)])], [catalog.cultures]);
  const presetLabel = catalog.style_presets.find((p) => p.id === form.style_preset)?.label || '';
  const blocker = source === 'original'
    ? (form.idea.trim().length < 10 ? 'Describe your idea in at least one sentence' : '')
    : !(adapt.source_id || adapt.source_title.trim()) ? 'Pick a story to adapt'
      : !adapt.culture_id ? 'Choose the target language & culture'
        : !adapt.originality_confirmed ? 'Tick the originality pledge' : '';

  async function submit() {
    if ((project.scenes || []).length && !window.confirm('Rewrite the screenplay? This replaces the current cast and shot list.')) return;
    setSending(true);
    try {
      await startAutoDirect(project.id, { ...form, adaptation: source === 'adapt' ? adapt : null });
      toast.success('Your AI director is writing the screenplay');
      onStarted();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  }

  return (
    <Card className="border-neutral-800 bg-neutral-900/40 text-neutral-100" data-testid="auto-director-panel">
      <CardContent className="space-y-5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold"><Sparkles size={16} className="text-amber-300" /> AI Director</div>
            <p className="text-xs text-neutral-500">One idea in → screenplay, locked cast & locations, shot list and camera plan out.</p>
          </div>
          <Seg value={source} onChange={setSource} testId="auto-source"
            options={[['original', 'Original idea', Lightbulb], ['adapt', 'Adapt a famous story', Globe2]]} />
        </div>

        {source === 'original' ? (
          <Textarea rows={4} value={form.idea} onChange={(e) => set({ idea: e.target.value })} data-testid="auto-idea-input"
            placeholder="e.g. A Manila street-food vendor discovers her late grandmother's recipe book is a map to a hidden family fortune — and three rival relatives want it too."
            className="border-neutral-800 bg-neutral-950 text-sm" />
        ) : (
          <>
            <AdaptationPicker catalog={catalog} value={adapt} onChange={setAdapt} />
            <Textarea rows={2} value={form.idea} onChange={(e) => set({ idea: e.target.value })} data-testid="auto-adapt-direction"
              placeholder="Optional extra direction — e.g. set it during Sinulog festival, make the villain sympathetic…"
              className="border-neutral-800 bg-neutral-950 text-sm" />
          </>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">Format</div>
            <Seg value={form.format} onChange={(v) => set({ format: v })} testId="auto-format" options={[['film', 'Film'], ['series', 'Series']]} />
          </div>
          {form.format === 'series' && (
            <div className="space-y-3">
              <div className="flex justify-between text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                <span>Episodes</span><span className="text-amber-300" data-testid="auto-episodes-value">{form.episodes_count}</span>
              </div>
              <Slider min={1} max={12} step={1} value={[form.episodes_count]} onValueChange={([v]) => set({ episodes_count: v })} data-testid="auto-episodes-slider" />
            </div>
          )}
          <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">Shots per {form.format === 'series' ? 'episode' : 'film'}</div>
            <select value={form.scenes_per_episode} onChange={(e) => set({ scenes_per_episode: Number(e.target.value) })} data-testid="auto-shots-select"
              className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs text-neutral-200">
              {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n} shots</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">Shot length</div>
            <select value={form.scene_duration} onChange={(e) => set({ scene_duration: Number(e.target.value) })} data-testid="auto-duration-select"
              className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs text-neutral-200">
              {[4, 5, 6, 8, 10, 15].map((n) => <option key={n} value={n}>{n} seconds</option>)}
            </select>
          </div>
          <CineCombo label="Genre" value={form.genre} options={catalog.genres} onChange={(v) => set({ genre: v })} testId="auto-genre" />
          <CineCombo label="Tone" value={form.tone} options={catalog.tones} onChange={(v) => set({ tone: v })} testId="auto-tone" />
          {source === 'original' && (
            <CineCombo label="Dialogue language" value={form.language} options={languages} onChange={(v) => set({ language: v || 'English' })} testId="auto-language" />
          )}
          <CineCombo label="Visual style" value={presetLabel} options={catalog.style_presets.map((p) => p.label)} testId="auto-style"
            onChange={(label) => set({ style_preset: catalog.style_presets.find((p) => p.label === label)?.id || '' })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
          <div className="text-xs text-neutral-400" data-testid="auto-summary">
            {eps} {eps > 1 ? 'episodes' : 'film'} · {shots} shots · ≈{Math.floor(runtime / 60)}m {runtime % 60}s runtime · camera, lens & lighting planned per shot
          </div>
          <div className="flex items-center gap-2">
            {blocker && <span className="text-[11px] text-amber-300/80" data-testid="auto-blocker-hint">{blocker}</span>}
            <CreditPill cost={eps + 1} label="to write" data-testid="auto-credit-pill" />
            <Button onClick={submit} disabled={running || sending || !!blocker} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="auto-direct-submit">
              <Sparkles size={14} className="mr-1" /> {running ? 'Writing…' : (project.scenes || []).length ? 'Rewrite screenplay' : 'Write screenplay & lock cast'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
